import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { handleCancelSubscription, handleGetMySubscription, handleRestoreSubscription, handleCreatePortalSession, handleCancelPendingDowngrade } from "../../../redux/member-slices/payment.slice";
import { useDispatch } from "react-redux";

const Subscription = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const getSubscription = async () => {
    setLoading(true);

    const response = await dispatch(handleGetMySubscription());

    if (response?.payload) {
      if (response?.payload?.hasSubscription) {
        setSubscription(response.payload);
      } else {
        setSubscription(null);
      }
    } else {
      setSubscription(null);
    }

    setLoading(false);
  };

  const handleOpenCancelDialog = () => {
    setOpenCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    if (!cancelling) {
      setOpenCancelDialog(false);
    }
  };

  const cancelSubscription = async () => {
    setCancelling(true);
    const response = await dispatch(handleCancelSubscription());
    setCancelling(false);
    setOpenCancelDialog(false);

    if (response?.payload?.success) {
      getSubscription();
    }
  };

  const restoreSubscription = async () => {
    const response = await dispatch(handleRestoreSubscription());

    if (response?.payload?.success) {
      getSubscription();
    }
  };

  const handleCancelDowngrade = async () => {
    const response = await dispatch(handleCancelPendingDowngrade());

    if (response?.payload?.success) {
      getSubscription();
    }
  };

  const updateSubscriptionCard = async () => {
    const response = await dispatch(handleCreatePortalSession());
    if (response?.payload?.url) {
      window.location.href = response.payload.url;
    }
  };

  useEffect(() => {
    getSubscription();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 300,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!subscription) {
    return (
      <Card elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
        <CardContent>
          <Stack spacing={3} alignItems="center" py={4}>
            <Typography variant="h5" fontWeight={600}>
              No Active Plan
            </Typography>

            <Typography
              color="text.secondary"
              textAlign="center"
              maxWidth={500}
            >
              You do not have an active subscription. You are currently on the Free plan. Upgrade to a subscription plan to unlock premium features and direct specialist access.
            </Typography>

            <Button
              variant="contained"
              onClick={() => navigate("/pricing")}
              sx={{
                bgcolor: "#2F4250",
                color: "#fff",
                px: 4,
                py: 1,
                "&:hover": {
                  bgcolor: "#22313D",
                },
              }}
            >
              Purchase Subscription
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
      <CardContent>
        <Stack spacing={3}>
          <Typography
            variant="h5"
            fontWeight={600}
            color="primary"
          >
            My Subscription
          </Typography>

          <Divider />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
              },
              gap: 3,
            }}
          >
            <InfoItem
              label="Plan"
              value={subscription?.plan?.name}
            />

            <InfoItem
              label="Billing Cycle"
              value={subscription?.plan?.billingCycle}
            />

            <InfoItem
              label="Status"
              value={subscription?.status}
            />

            <InfoItem
              label="Auto Renew"
              value={subscription?.autoRenew ? "Yes" : "No"}
            />

            <InfoItem
              label="Start Date"
              value={moment(subscription?.startDate).format(
                "DD MMM YYYY"
              )}
            />

            <InfoItem
              label="End Date"
              value={moment(subscription?.endDate).format(
                "DD MMM YYYY"
              )}
            />

            <InfoItem
              label="Days Remaining"
              value={subscription?.daysRemaining}
            />

            <InfoItem
              label="Webinars Used"
              value={subscription?.webinarsUsed}
            />

            <InfoItem
              label="Chat Used Days"
              value={subscription?.chatUsedDays}
            />

            <InfoItem
              label="Card Details"
              value={subscription?.cardLast4 ? `${subscription.cardBrand ? subscription.cardBrand.toUpperCase() : "Card"} ending in ${subscription.cardLast4}` : "-"}
            />
          </Box>

          {subscription?.pendingDowngrade && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#fff8e1",
                border: "1px solid #ffe082",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <Typography color="warning.dark" fontWeight={600}>
                Scheduled Plan Downgrade
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You have scheduled a downgrade to the <strong>{subscription?.pendingPlan?.name} ({subscription?.pendingPlan?.billingCycle})</strong> plan. It will automatically take effect on <strong>{moment(subscription?.endDate).format("DD MMM YYYY")}</strong> at the end of your current billing period.
              </Typography>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={handleCancelDowngrade}
              >
                Cancel Scheduled Downgrade
              </Button>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {subscription?.cancelAtPeriodEnd ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 2,
                }}
              >
                <Typography color="error">
                  Your subscription has been cancelled and will end on{" "}
                  {moment(subscription?.endDate).format("DD MMM YYYY")}
                </Typography>

                <Button
                  variant="contained"
                  onClick={restoreSubscription}
                  sx={{
                    bgcolor: "#2F4250",
                    color: "#fff",
                    px: 3,
                    "&:hover": {
                      bgcolor: "#22313D",
                    },
                  }}
                >
                  Restore Subscription
                </Button>
              </Box>
            ) : (
              (subscription?.status === "active" || subscription?.status === "past_due") && (
                <>
                  <Button
                    variant="contained"
                    onClick={updateSubscriptionCard}
                    sx={{
                      bgcolor: "#2F4250",
                      color: "#fff",
                      px: 3,
                      "&:hover": {
                        bgcolor: "#22313D",
                      },
                    }}
                  >
                    Update Payment Method
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={handleOpenCancelDialog}
                  >
                    Cancel Subscription
                  </Button>
                </>
              )
            )}
          </Box>
        </Stack>

        <Dialog
          open={openCancelDialog}
          onClose={handleCloseCancelDialog}
          aria-labelledby="cancel-subscription-dialog-title"
          aria-describedby="cancel-subscription-dialog-description"
          PaperProps={{
            sx: {
              borderRadius: 2,
              p: 1,
              maxWidth: 460,
              width: "100%",
            },
          }}
        >
          <DialogTitle id="cancel-subscription-dialog-title" fontWeight={600}>
            Cancel Subscription
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="cancel-subscription-dialog-description">
              Are you sure you want to cancel your subscription? Your access will remain active until the end of your current billing period.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={handleCloseCancelDialog}
              variant="outlined"
              disabled={cancelling}
              sx={{
                color: "#2F4250",
                borderColor: "#e0e0e0",
                "&:hover": {
                  borderColor: "#2F4250",
                  bgcolor: "transparent",
                },
              }}
            >
              Keep Subscription
            </Button>
            <Button
              onClick={cancelSubscription}
              color="error"
              variant="contained"
              disabled={cancelling}
              disableElevation
              startIcon={cancelling ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {cancelling ? "Cancelling..." : "Confirm Cancel"}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const InfoItem = ({ label, value }) => {
  return (
    <Box>
      <Typography
        fontSize={14}
        color="text.secondary"
        mb={0.5}
      >
        {label}
      </Typography>

      <Typography
        fontWeight={600}
        textTransform="capitalize"
      >
        {value ?? "-"}
      </Typography>
    </Box>
  );
};

export default Subscription;